// ===================================================================
// src/app/shared/components/sidebar/sidebar.component.ts
// ===================================================================
import { Component, inject, signal, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';

import { AuthService } from '../../../core/services/auth.service';
import { UserRole } from '../../../features/users/models/user.model';
import { SystemAdminService } from '../../../core/services/system-admin.service';
import { ClinicSubscriptionService } from '../../../features/subscriptions/services/clinic-subscription.service';
import { SubscriptionStatus } from '../../../features/subscriptions/models/subscription.model';

interface MenuItem {
  title: string;
  icon: string;
  route: string;
  roles?: UserRole[];
  children?: MenuItem[];
  badge?: number;
  expanded?: boolean;
}


@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SidebarComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private systemAdminService = inject(SystemAdminService);
  private router = inject(Router);
  private subscriptionService = inject(ClinicSubscriptionService);

  // Signals
  currentUser = this.authService.currentUser;
  isOpen = signal(true);
  sidenavMode = signal<'side' | 'over'>('side');
  menuItems = signal<MenuItem[]>([]);
  hasActiveSubscription = signal<boolean | null>(null); // null = loading/unknown

  private eventListener?: () => void;

  ngOnInit(): void {
    this.setupMenuItems(); // Build full menu immediately
    this.checkSubscriptionStatus(); // Then update only subscription children
    this.handleResize();
    this.setupEventListeners();

    // مراقبة تغيير حجم النافذة
    window.addEventListener('resize', () => this.handleResize());
  }

  ngOnDestroy(): void {
    if (this.eventListener) {
      document.removeEventListener('toggleSidebar', this.eventListener);
    }
    window.removeEventListener('resize', () => this.handleResize());
  }

  private checkSubscriptionStatus(): void {
    const user = this.currentUser();

    if (user?.role === 'ADMIN' && user.clinicId) {
      this.subscriptionService.getSubscriptionByClinicId(user.clinicId, true).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const status = response.data.status;
            this.hasActiveSubscription.set(
              status === SubscriptionStatus.ACTIVE || status === SubscriptionStatus.TRIAL
            );
          } else {
            this.hasActiveSubscription.set(false);
          }
          this.updateSubscriptionMenuChildren();
        },
        error: () => {
          this.hasActiveSubscription.set(false);
          this.updateSubscriptionMenuChildren();
        }
      });
    }
  }

  private updateSubscriptionMenuChildren(): void {
    const items = this.menuItems();
    const subscriptionItem = items.find(item => item.title === 'اشتراكي');
    if (subscriptionItem) {
      subscriptionItem.children = this.buildSubscriptionChildren();
      this.menuItems.set([...items]); // Trigger signal update
    }
  }

  private setupMenuItems(): void {
    const items: MenuItem[] = [
      {
        title: 'لوحة التحكم',
        icon: 'dashboard',
        route: '/dashboard'
      },
      // ===================================================================
      // ADMIN SECTION - إدارة النظام (SYSTEM_ADMIN Only)
      // ===================================================================
      {
        title: 'إدارة النظام',
        icon: 'admin_panel_settings',
        route: '',
        roles: ['SYSTEM_ADMIN'],
        expanded: false,
        children: [
          {
            title: 'إدارة الإعلانات',
            icon: 'campaign',
            route: '/admin/announcements',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'سجلات المراجعة',
            icon: 'security',
            route: '/admin/audit/logs',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'إحصائيات المراجعة',
            icon: 'bar_chart',
            route: '/admin/audit/statistics',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'تحديد سياق العيادة',
            icon: 'business',
            route: '/system/select-clinic-context',
            roles: ['SYSTEM_ADMIN']
          }
        ]
      },

      {
        title: 'إدارة العيادات',
        icon: 'business',
        route: '/admin/clinics/management',
        roles: ['SYSTEM_ADMIN'],
      },

      // {
      //   title: 'إدارة العيادات',
      //   icon: 'business',
      //   route: '',
      //   roles: ['SYSTEM_ADMIN'],
      //   expanded: false,
      //   children: [
      //     {
      //       title: 'جميع العيادات',
      //       icon: 'list',
      //       route: '/admin/clinics/management',
      //       roles: ['SYSTEM_ADMIN']
      //     },
      //     {
      //       title: 'العيادات النشطة',
      //       icon: 'check_circle',
      //       route: '/admin/clinics/management?status=active',
      //       roles: ['SYSTEM_ADMIN']
      //     },
      //     {
      //       title: 'العيادات الموقوفة',
      //       icon: 'cancel',
      //       route: '/admin/clinics/management?status=inactive',
      //       roles: ['SYSTEM_ADMIN']
      //     }
      //   ]
      // },
      // ===================================================================
      // SUBSCRIPTION MANAGEMENT SECTION - إدارة الاشتراكات
      // ===================================================================
      {
        title: 'إدارة الاشتراكات',
        icon: 'subscriptions',
        route: '',
        roles: ['SYSTEM_ADMIN'],
        expanded: false,
        children: [
          {
            title: 'طلبات الاشتراك',
            icon: 'assignment',
            route: '/subscriptions/admin/requests',
            roles: ['SYSTEM_ADMIN'],
          },
          {
            title: 'الخطط والأسعار',
            icon: 'card_membership',
            route: '/subscriptions/admin/plans',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'إدارة الميزات',
            icon: 'extension',
            route: '/subscriptions/admin/features',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'اشتراكات العيادات',
            icon: 'business',
            route: '/subscriptions/admin/manage',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'اشتراكات منتهية قريباً',
            icon: 'warning',
            route: '/subscriptions/admin/manage/expiring/soon',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'فترات تجريبية منتهية',
            icon: 'schedule',
            route: '/subscriptions/admin/manage/trials/ending',
            roles: ['SYSTEM_ADMIN']
          },
          {
            title: 'تحليلات الاشتراكات',
            icon: 'analytics',
            route: '/subscriptions/admin/analytics',
            roles: ['SYSTEM_ADMIN']
          }
        ]
      },
      // ===================================================================
      // SUBSCRIPTION INFO - For ADMIN users (Clinic Admins)
      // ===================================================================
      {
        title: 'اشتراكي',
        icon: 'card_membership',
        route: '',
        roles: ['ADMIN'],
        expanded: false,
        /* children: [
          {
            title: 'تفاصيل الاشتراك',
            icon: 'info',
            route: '/subscriptions/my-subscription',
            roles: ['ADMIN']
          },
          {
            title: 'الاستخدام والحصص',
            icon: 'data_usage',
            route: '/subscriptions/usage',
            roles: ['ADMIN']
          },
          {
            title: 'مقارنة الخطط',
            icon: 'compare',
            route: '/subscriptions/plans/compare',
            roles: ['ADMIN']
          }
        ] */
        children: this.buildSubscriptionChildren()
      },
      {
        title: 'المواعيد',
        icon: 'event',
        expanded: false,
        // roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST'],
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN', 'DOCTOR', 'RECEPTIONIST', 'NURSE'
        ] as UserRole[],
        route: '',
        children: [
          {
            title: 'جميع المواعيد',
            icon: 'list',
            route: '/appointments',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'ترتيب مواعيد اليوم',
            icon: 'format_list_numbered_rtl',
            route: '/appointments/arrangement',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'موعد جديد',
            icon: 'add_circle',
            route: '/appointments/new',
            roles: ['ADMIN', 'DOCTOR', 'RECEPTIONIST']
          },
          {
            title: 'التقويم',
            icon: 'calendar_today',
            route: '/appointments/calendar',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'RECEPTIONIST']
          },
          // {
          //   title: 'مواعيد اليوم',
          //   icon: 'today',
          //   route: '/appointments/today',
          //   roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          // },
          {
            title: 'المرضى المتأخرين',
            icon: 'schedule_send',
            route: '/appointments/overdue-returns',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'RECEPTIONIST']
          },
          {
            title: 'تحليلات المتابعة',
            icon: 'analytics',
            route: '/appointments/follow-up-analytics',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'RECEPTIONIST']
          }
        ],
      },
      {
        title: 'المرضى',
        icon: 'people',
        expanded: false,
        // roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST'],
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN', 'DOCTOR', 'RECEPTIONIST', 'NURSE'
        ] as UserRole[],
        route: '',
        badge: 5,
        children: [
          {
            title: 'قائمة المرضى',
            icon: 'list',
            route: '/patients',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'إضافة مريض',
            icon: 'person_add',
            route: '/patients/new',
            roles: ['ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          }
        ]
      },
      // ===================================================================
      // SCHEDULES MENU
      // ===================================================================
      {
        title: 'جداول الأطباء',
        icon: 'schedule',
        route: '/schedules',
        // roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST'],
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN', 'DOCTOR', 'RECEPTIONIST', 'NURSE'
        ] as UserRole[],
        badge: 5,
        children: [
          {
            title: 'عرض الجداول',
            icon: 'view_list',
            route: '/schedules',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'التقويم',
            icon: 'calendar_month',
            route: '/schedules/calendar',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'فحص التوفر',
            icon: 'check_circle',
            route: '/schedules/availability',
            roles: ['ADMIN', 'DOCTOR', 'NURSE', 'RECEPTIONIST']
          },
          {
            title: 'إنشاء جدولة',
            icon: 'add_circle',
            route: '/schedules/create',
            roles: ['ADMIN', 'DOCTOR']
          },
          {
            title: 'إدارة عدم التوفر',
            icon: 'event_busy',
            route: '/schedules/unavailability/create',
            roles: ['ADMIN', 'SYSTEM_ADMIN']
          }
        ]
      },
      /* {
        title: 'السجلات الطبية',
        icon: 'description',
        expanded: false,
        // roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE'],
        roles: ['SYSTEM_ADMIN'],
        route: '',
        children: [
          {
            title: 'قائمة السجلات',
            icon: 'list',
            route: '/medical-records/list',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE']
          },
          {
            title: 'سجل جديد',
            icon: 'add_circle',
            route: '/medical-records/new',
            roles: ['DOCTOR', 'ADMIN', 'SYSTEM_ADMIN']
          },
          {
            title: 'البحث المتقدم',
            icon: 'search',
            route: '/medical-records/search',
            roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'NURSE']
          },
          {
            title: 'الإحصائيات',
            icon: 'analytics',
            route: '/medical-records/statistics',
            roles: ['DOCTOR', 'ADMIN', 'SYSTEM_ADMIN']
          }
        ]
      }, */
      {
        title: 'الفواتير',
        icon: 'receipt_long',
        route: '/invoices',
        // roles: ['SYSTEM_ADMIN', 'ADMIN', 'DOCTOR', 'RECEPTIONIST', 'NURSE'],
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN', 'DOCTOR', 'RECEPTIONIST', 'NURSE'
        ] as UserRole[]
      },
      {
        title: 'إدارة المستخدمين',
        icon: 'admin_panel_settings',
        route: '/users',
        // roles: ['ADMIN', 'SYSTEM_ADMIN'],
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN'
        ] as UserRole[],
        children: [
          {
            title: 'جميع المستخدمين',
            icon: 'people',
            route: '/users/list',
            roles: ['ADMIN', 'SYSTEM_ADMIN']
          },
          {
            title: 'إحصائيات المستخدمين',
            icon: 'analytics',
            route: '/users/statistics',
            roles: ['ADMIN', 'SYSTEM_ADMIN']
          },
        ]
      },
      {
        title: 'سجل الأنشطة',
        icon: 'history',
        route: '/admin/activities',
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN'
        ] as UserRole[]
      },
      {
        icon: 'settings',
        title: 'الإعدادات',
        route: '/settings',
        roles: [
          ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
          'ADMIN'
        ] as UserRole[],
        children: [
          {
            icon: 'medical_services',
            title: 'إعدادات العيادة',
            route: '/settings',
            roles: [
              ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
              'ADMIN'
            ] as UserRole[],
          },
          {
            title: 'الأرقام المحظورة',
            icon: 'block',
            route: '/blocked-phones',
            roles: [
              ...(this.systemAdminService.actingClinicId() ? ['SYSTEM_ADMIN'] : []),
              'ADMIN'
            ] as UserRole[],
          },
        ]
      },

      // التقييم والملاحظات - Feedback
      {
        title: 'التقييم والملاحظات',
        icon: 'feedback',
        route: '/feedback'
      }

    ];

    this.menuItems.set(items);
  }

  private buildSubscriptionChildren(): MenuItem[] {
    const hasSubscription = this.hasActiveSubscription();

    // Clinic WITHOUT active subscription
    if (hasSubscription === false) {
      return [
        {
          title: 'طلب اشتراك جديد',
          icon: 'add_card',
          route: '/subscriptions/request',
          roles: ['ADMIN']
        },
        {
          title: 'مقارنة الخطط',
          icon: 'compare',
          route: '/subscriptions/plans/compare',
          roles: ['ADMIN']
        }
      ];
    }

    // Clinic WITH active subscription (or still loading)
    return [
      {
        title: 'تفاصيل الاشتراك',
        icon: 'info',
        route: '/subscriptions/my-subscription',
        roles: ['ADMIN']
      },
      {
        title: 'الاستخدام والحصص',
        icon: 'data_usage',
        route: '/subscriptions/usage',
        roles: ['ADMIN']
      },
      {
        title: 'مقارنة الخطط',
        icon: 'compare',
        route: '/subscriptions/plans/compare',
        roles: ['ADMIN']
      }
    ];
  }

  private setupEventListeners(): void {
    this.eventListener = () => {
      this.isOpen.update(open => !open);
    };

    document.addEventListener('toggleSidebar', this.eventListener);
  }

  private handleResize(): void {
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      this.sidenavMode.set('over');
      this.isOpen.set(false);
    } else {
      this.sidenavMode.set('side');
      this.isOpen.set(true);
    }
  }

  toggleMenuItem(item: MenuItem): void {
    if (item.children) {
      item.expanded = !item.expanded;
    }
  }

  hasPermission(roles?: UserRole[]): boolean {
    if (!roles || roles.length === 0) {
      return true;
    }

    return this.authService.hasRole(roles);
  }

  getRoleDisplayName(role?: string): string {
    const roleNames: { [key: string]: string } = {
      'SYSTEM_ADMIN': 'مدير النظام',
      'ADMIN': 'مدير العيادة',
      'DOCTOR': 'طبيب',
      'NURSE': 'ممرض/ممرضة',
      'RECEPTIONIST': 'موظف استقبال'
    };

    return roleNames[role || ''] || 'مستخدم';
  }
}
